import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from 'axios';
import { BASE_URL } from "../constants/config";
import { getTokensFromStore, checkIfTokenExpired } from "../utilities/tokens";
import { refreshTokens } from "../actions/usersActions";
import store from "../utilities/store";
import defaultTeamLogo from "../images/defaultTeamLogo.png";
import { toServerEventTime } from '../utilities/matchTime';
import '../styles/onlineMatch.css';

// ═══════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════

const FORMATIONS_BY_FORMAT = {
  '4x4':  ['1-2'],
  '5x5':  ['2-2'],
  '6x6':  ['2-1-2', '2-2-1', '3-1-1'],
  '7x7':  ['3-1-2', '2-2-2', '3-2-1'],
  '8x8':  ['3-3-1', '3-2-2', '2-4-1', '2-3-2', '2-2-3', '1-3-3'],
  '9x9':  ['4-2-2', '4-3-1', '4-1-3', '3-3-2', '3-4-1'],
  '11x11': ['4-4-2', '4-3-3', '5-3-2'],
};

const MATCH_FORMATS = Object.keys(FORMATIONS_BY_FORMAT);

const LIVE_EVENT_TYPES = {
  1:  { label: 'Гол',           icon: '⚽',    hasAssist: true,  scoreDelta: 'own',      stat: 'goals' },
  6:  { label: 'Пен (забит)',   icon: '⚽🥅',  hasAssist: false, scoreDelta: 'own',      stat: null },
  7:  { label: 'Пен (незабит)', icon: '❌🥅',  hasAssist: false, scoreDelta: null,        stat: null },
  8:  { label: 'Автогол',       icon: '⚽🔴',  hasAssist: false, scoreDelta: 'opponent',  stat: 'autogoals' },
  16: { label: '10м (забит)',   icon: '⚽🔟',  hasAssist: false, scoreDelta: 'own',      stat: null },
  17: { label: '10м (незабит)', icon: '❌🔟',  hasAssist: false, scoreDelta: null,        stat: null },
  18: { label: '6м (забит)',    icon: '⚽6️⃣',  hasAssist: false, scoreDelta: 'own',      stat: null },
  19: { label: '6м (незабит)',  icon: '❌6️⃣',  hasAssist: false, scoreDelta: null,        stat: null },
  3:  { label: 'ЖК',            icon: '🟨',    hasAssist: false, scoreDelta: null,        stat: 'yellow_cards' },
  13: { label: '2-я ЖК',         icon: '🟨🟨',  hasAssist: false, scoreDelta: null,        stat: 'yellow_cards' },
  4:  { label: 'КК',            icon: '🟥',    hasAssist: false, scoreDelta: null,        stat: 'red_cards' },
  15: { label: 'Замена',        icon: '🔄',    hasAssist: false, scoreDelta: null,        stat: null, isSub: true },
};

const QUICK_EVENTS = [1, 8, 3, 4];
const MORE_EVENTS = [6, 7, 16, 17, 18, 19, 13, 15];

const SYSTEM_EVENT_LABELS = {
  9:  '▶ Начало матча',
  10: '⏸ Перерыв',
  11: '🏁 Конец матча',
};

const POSITION_LABELS = { 1: 'НАП', 2: 'ПЗ', 3: 'ЗАЩ', 4: 'ВРТ', 5: 'УНВ' };
const getPositionShort = (player, slotIndex) => {
  if (slotIndex === 0) return 'ВРТ';
  if (player?.position_id) return POSITION_LABELS[player.position_id] || '';
  if (player?.match_position) return POSITION_LABELS[player.match_position] || '';
  return '';
};

function generateFieldPositions(formationStr) {
  const lines = formationStr.split('-').map(Number);
  const positions = [{ top: 88, left: 50 }]; // GK
  const yMap = { 1: [50], 2: [68, 28], 3: [72, 48, 22] };
  const yPositions = yMap[lines.length] || yMap[2];
  lines.forEach((count, i) => {
    const y = yPositions[i];
    for (let j = 0; j < count; j++) {
      positions.push({ top: y, left: (j + 1) * 100 / (count + 1) });
    }
  });
  return positions;
}

function calcScore(events, homeTeamId) {
  let home = 0, guest = 0;
  events.forEach(e => {
    const t = LIVE_EVENT_TYPES[e.event_type];
    if (!t || !t.scoreDelta) return;
    if (t.scoreDelta === 'own') { e.team_id === homeTeamId ? home++ : guest++; }
    else if (t.scoreDelta === 'opponent') { e.team_id === homeTeamId ? guest++ : home++; }
  });
  return { home, guest };
}

// ═══════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════

export default function OnlineMatch({ visible, closeModal, matchData, matchApplication, onMatchFinish }) {

  // ─── Phase ───
  const [phase, setPhase] = useState('roster');

  // ─── Roster ───
  const [selectedPlayers, setSelectedPlayers] = useState({});

  // ─── Lineup ───
  const [matchFormat, setMatchFormat] = useState('');
  const [homeFormation, setHomeFormation] = useState('');
  const [guestFormation, setGuestFormation] = useState('');
  // Starting lineup: { [slotIndex]: playerId }
  const [homeLineup, setHomeLineup] = useState({});
  const [guestLineup, setGuestLineup] = useState({});
  const [pickingSlot, setPickingSlot] = useState(null);

  // ─── Timer ───
  // idle | half1 | paused1 | halftime | half2 | paused2 | ended
  const [matchStatus, setMatchStatus] = useState('idle');
  const [displaySeconds, setDisplaySeconds] = useState(0);
  const [editingTimer, setEditingTimer] = useState(false);
  const [editMin, setEditMin] = useState('0');
  const [editSec, setEditSec] = useState('0');
  const timerRef = useRef(null);

  // ─── Events ───
  const [matchEvents, setMatchEvents] = useState([]);
  const [eventModalOpen, setEventModalOpen] = useState(false);
  const [eventModalTeamSide, setEventModalTeamSide] = useState('home');
  const [eventType, setEventType] = useState(1);
  const [eventPlayer, setEventPlayer] = useState('');
  const [eventAssist, setEventAssist] = useState('');
  const [eventMinute, setEventMinute] = useState('');
  const [eventSecond, setEventSecond] = useState('');
  const [showMoreEvents, setShowMoreEvents] = useState(false);
  const [editingEventId, setEditingEventId] = useState(null);
  // substitution player for event type 15
  const [eventSubPlayer, setEventSubPlayer] = useState('');
  // dragging player id (for live field drag-move/substitution)
  const [draggingPlayer, setDraggingPlayer] = useState(null);
  // drag-over target for lineup phase
  const [dragOverTarget, setDragOverTarget] = useState(null);

  // ─── Jersey colors ───
  const [homeColor, setHomeColor] = useState('#3498db');
  const [guestColor, setGuestColor] = useState('#e74c3c');

  // ─── Notification ───
  const [notification, setNotification] = useState('');

  // ─── Loading state for server restore ───
  const [isRestoring, setIsRestoring] = useState(false);

  // ═══ DERIVED ═══
  const halfTime = matchData ? parseInt(matchData.half_time) || 30 : 30;
  const homeTeamId = matchData?.home_team_id;
  const guestTeamId = matchData?.guest_team_id;
  const currentMinute = Math.floor(displaySeconds / 60);
  const currentSecond = displaySeconds % 60;
  const score = calcScore(matchEvents, homeTeamId);

  // ═══ TIMER EFFECT ═══
  useEffect(() => {
    if (matchStatus === 'half1' || matchStatus === 'half2') {
      timerRef.current = setInterval(() => setDisplaySeconds(p => p + 1), 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [matchStatus]);

  // ═══ SESSION STORAGE (persist setup for one match, clears on page refresh) ═══
  const SESSION_KEY = 'olm_match_setup';

  const saveToSession = (data) => {
    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(data));
    } catch (e) { /* ignore */ }
  };

  const loadFromSession = () => {
    try {
      const raw = sessionStorage.getItem(SESSION_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) { return null; }
  };

  const clearSession = () => {
    try { sessionStorage.removeItem(SESSION_KEY); } catch (e) { /* ignore */ }
  };

  // ═══ RESET ON OPEN ═══
  useEffect(() => {
    if (visible && matchApplication && matchData) {
      // First check if match is already in progress on the server
      const tryRestore = async () => {
        const restored = await restoreMatchFromServer();
        if (restored) return; // Restored from server — done

        // Not in progress on server — check local session
        const saved = loadFromSession();
        if (saved && saved.matchId === matchData?.id) {
          setSelectedPlayers(saved.selectedPlayers || {});
          setPhase(saved.phase || 'roster');
          setMatchFormat(saved.matchFormat || '');
          setHomeFormation(saved.homeFormation || '');
          setGuestFormation(saved.guestFormation || '');
          setHomeLineup(saved.homeLineup || {});
          setGuestLineup(saved.guestLineup || {});
          setHomeColor(saved.homeColor || '#3498db');
          setGuestColor(saved.guestColor || '#e74c3c');
        } else {
          clearSession();
          const sel = {};
          matchApplication.forEach(p => { sel[p.id] = !!p.active; });
          setSelectedPlayers(sel);
          setPhase('roster');
          setMatchFormat('');
          setHomeFormation('');
          setGuestFormation('');
          setHomeLineup({});
          setGuestLineup({});
          setHomeColor('#3498db');
          setGuestColor('#e74c3c');
        }
        // Reset transient state for non-restored case
        setMatchStatus('idle');
        setDisplaySeconds(0);
        setMatchEvents([]);
        setNotification('');
        setEventModalOpen(false);
        setEditingTimer(false);
        setEditingEventId(null);
        setPickingSlot(null);
        setEventSubPlayer('');
        setDraggingPlayer(null);
        setDragOverTarget(null);
      };
      tryRestore();
    }
  }, [visible]); // eslint-disable-line react-hooks/exhaustive-deps

  // ═══ AUTO-SAVE setup to session on change ═══
  useEffect(() => {
    if (!visible || !matchData) return;
    // Only save pre-live setup (roster & lineup phases)
    if (phase === 'live' || phase === 'ended') return;
    saveToSession({
      matchId: matchData.id,
      phase,
      selectedPlayers,
      matchFormat,
      homeFormation,
      guestFormation,
      homeLineup,
      guestLineup,
      homeColor,
      guestColor,
    });
  }, [visible, matchData, phase, selectedPlayers, matchFormat, homeFormation, guestFormation, homeLineup, guestLineup, homeColor, guestColor]);

  // ═══ HELPERS ═══
  const getTeamPlayers = useCallback((teamId) => {
    if (!matchApplication) return [];
    return matchApplication.filter(p => p.team_id === teamId || p.isDouble === teamId);
  }, [matchApplication]);

  const getSelectedTeamPlayers = useCallback((teamId) => getTeamPlayers(teamId).filter(p => selectedPlayers[p.id]), [getTeamPlayers, selectedPlayers]);
  const getSelectedCount = (teamId) => getTeamPlayers(teamId).filter(p => selectedPlayers[p.id]).length;
  const findPlayerById = (id) => matchApplication ? matchApplication.find(p => p.id === id) : null;
  const isMatchActive = () => ['half1', 'half2', 'paused1', 'paused2'].includes(matchStatus);

  // Check if player has red card (direct or 2nd yellow) -> banned
  const isPlayerBanned = useCallback((playerId) => {
    const pEvents = matchEvents.filter(e => e.player_id === playerId && !e.isSystem);
    const hasRed = pEvents.some(e => e.event_type === 4);
    const has2ndYellow = pEvents.some(e => e.event_type === 13);
    return hasRed || has2ndYellow;
  }, [matchEvents]);

  // Check if player is currently substituted out (supports reverse subs)
  const isPlayerSubbedOut = useCallback((playerId) => {
    const subsOut = matchEvents.filter(e => e.event_type === 15 && e.player_id === playerId && !e.isSystem).length;
    const subsIn  = matchEvents.filter(e => e.event_type === 15 && e.additional_player_id === playerId && !e.isSystem).length;
    return subsOut > subsIn;
  }, [matchEvents]);

  // Count yellow cards for a player
  const getPlayerYellows = useCallback((playerId) => {
    return matchEvents.filter(e => e.player_id === playerId && (e.event_type === 3 || e.event_type === 13) && !e.isSystem).length;
  }, [matchEvents]);

  // Check if player can receive events
  const isPlayerAvailable = useCallback((playerId) => {
    return !isPlayerBanned(playerId) && !isPlayerSubbedOut(playerId);
  }, [isPlayerBanned, isPlayerSubbedOut]);

  // Get bench players (selected but not on field) for live phase
  const getLiveBenchPlayers = useCallback((side) => {
    const teamId = side === 'home' ? homeTeamId : guestTeamId;
    const lineup = side === 'home' ? homeLineup : guestLineup;
    const onFieldIds = new Set(Object.values(lineup));
    return getSelectedTeamPlayers(teamId).filter(p => !onFieldIds.has(p.id) && !isPlayerBanned(p.id));
  }, [homeTeamId, guestTeamId, homeLineup, guestLineup, getSelectedTeamPlayers, isPlayerBanned]);

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  const teamLogo = (img) => img ? BASE_URL.url + img : defaultTeamLogo;

  const showNotif = (msg) => {
    setNotification(msg);
    setTimeout(() => setNotification(''), 3000);
  };

  const statusLabel = () => {
    switch (matchStatus) {
      case 'idle': return 'Ожидание';
      case 'half1': return '1-й тайм';
      case 'paused1': return '1-й тайм (пауза)';
      case 'halftime': return 'Перерыв';
      case 'half2': return '2-й тайм';
      case 'paused2': return '2-й тайм (пауза)';
      case 'ended': return 'Матч завершён';
      default: return '';
    }
  };

  // ═══ ROSTER ACTIONS ═══
  const togglePlayer = (id) => setSelectedPlayers(p => ({ ...p, [id]: !p[id] }));

  const selectAll = (teamId) => {
    const players = getTeamPlayers(teamId);
    const allSel = players.every(p => selectedPlayers[p.id]);
    const upd = {};
    players.forEach(p => { upd[p.id] = !allSel; });
    setSelectedPlayers(p => ({ ...p, ...upd }));
  };

  const goToLineup = () => {
    if (getSelectedCount(homeTeamId) < 1 || getSelectedCount(guestTeamId) < 1) {
      showNotif('Выберите хотя бы по одному игроку в каждой команде');
      return;
    }
    setPhase('lineup');
  };

  // ═══ LINEUP ACTIONS ═══
  const onFormatChange = (fmt) => {
    setMatchFormat(fmt);
    setHomeFormation('');
    setGuestFormation('');
    setHomeLineup({});
    setGuestLineup({});
  };

  const onFormationChange = (side, f) => {
    if (side === 'home') { setHomeFormation(f); setHomeLineup({}); }
    else { setGuestFormation(f); setGuestLineup({}); }
  };

  const getLineup = (side) => side === 'home' ? homeLineup : guestLineup;
  const setLineupSide = (side, val) => side === 'home' ? setHomeLineup(val) : setGuestLineup(val);
  const getFormation = (side) => side === 'home' ? homeFormation : guestFormation;

  const getBenchPlayers = (side) => {
    const teamId = side === 'home' ? homeTeamId : guestTeamId;
    const lineup = getLineup(side);
    const assignedIds = new Set(Object.values(lineup));
    return getSelectedTeamPlayers(teamId).filter(p => !assignedIds.has(p.id));
  };

  const handleSlotClick = (side, slotIndex) => {
    const lineup = getLineup(side);
    if (lineup[slotIndex]) {
      const updated = { ...lineup };
      delete updated[slotIndex];
      setLineupSide(side, updated);
      setPickingSlot(null);
    } else {
      setPickingSlot({ teamSide: side, slotIndex });
    }
  };

  const assignPlayerToSlot = (side, slotIndex, playerId) => {
    const lineup = getLineup(side);
    const updated = { ...lineup };
    Object.keys(updated).forEach(k => { if (updated[k] === playerId) delete updated[k]; });
    updated[slotIndex] = playerId;
    setLineupSide(side, updated);
    setPickingSlot(null);
  };

  const autoFillLineup = (side) => {
    const teamId = side === 'home' ? homeTeamId : guestTeamId;
    const formation = getFormation(side);
    if (!formation) return;
    const positions = generateFieldPositions(formation);
    const players = getSelectedTeamPlayers(teamId);
    const newLineup = {};
    positions.forEach((_, i) => { if (i < players.length) newLineup[i] = players[i].id; });
    setLineupSide(side, newLineup);
    setPickingSlot(null);
  };

  const goToLive = () => {
    if (!matchFormat) { showNotif('Выберите формат матча'); return; }
    if (!homeFormation || !guestFormation) { showNotif('Выберите схему для обеих команд'); return; }
    setPhase('live');
  };

  // ═══ TIMER ACTIONS ═══
  const addSystemEvent = (eventId, seconds) => {
    const min = Math.floor(seconds / 60);
    const sec = seconds % 60;
    setMatchEvents(prev => [...prev, {
      id: Date.now() + Math.random(),
      event_type: eventId,
      player_id: null, player_name: null, player_number: null,
      team_id: null, team_name: null,
      minute: min, second: sec,
      time: toServerEventTime(min, sec),
      time_number: min >= halfTime ? 2 : 1,
      assist_player_id: null, assist_player_name: null,
      isSystem: true,
    }]);
  };

  // ═══ API helpers with auth ═══
  const getAuthHeaders = async () => {
    const isExpired = checkIfTokenExpired();
    if (isExpired) {
      await store.dispatch(refreshTokens());
    }
    const tokens = getTokensFromStore();
    return {
      'Authorization': 'Bearer ' + (tokens ? tokens.access_token : ''),
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    };
  };

  // ═══ API: Change Match State ═══
  const callChangeState = async (condition) => {
    if (!matchData?.id) return;
    try {
      const headers = await getAuthHeaders();
      await axios.post(BASE_URL.url + '/matches/changeState', {
        match_id: matchData.id,
        condition: condition,
        team_id: homeTeamId,
      }, { headers });
    } catch (err) {
      console.error('changeState error:', err);
      showNotif('Ошибка смены статуса матча');
    }
  };

  // ═══ API: Set Event ═══
  const callSetEvent = async (evtData) => {
    if (!matchData?.id) return null;
    try {
      const headers = await getAuthHeaders();
      const res = await axios.post(BASE_URL.url + '/matches/setEvent', {
        match_id: matchData.id,
        event_id: evtData.event_type,
        player_id: evtData.player_id,
        team_id: evtData.team_id,
        league_id: matchData.league_id,
        time: evtData.time,
        additional_player_id: evtData.additional_player_id || (evtData.assist_player_id || null),
        time_number: evtData.time_number,
      }, { headers });
      return res.data;
    } catch (err) {
      console.error('setEvent error:', err);
      showNotif('Ошибка отправки события на сервер');
      return null;
    }
  };

  // ═══ API: Remove Event ═══
  const callRemoveEvent = async (evt) => {
    if (!matchData?.id || !evt.server_id) {
      console.warn('removeEvent skipped: no server_id, event was not synced. Event:', evt);
      showNotif('Не удалось удалить событие на сервере: нет server_id');
      return false;
    }
    try {
      const headers = await getAuthHeaders();
      await axios.post(BASE_URL.url + '/matches/removeEvent', {
        match_id: matchData.id,
        event_id: evt.server_id,
        event_type: evt.event_type,
        team_id: evt.team_id,
        additional_event_id: evt.additional_event_id || null,
      }, { headers });
      return true;
    } catch (err) {
      console.error('removeEvent error:', err);
      showNotif('Ошибка удаления события на сервере');
      return false;
    }
  };

  // ═══ API: Change Event ═══
  const callChangeEvent = async (evtData, previousEventType, previousTeamId) => {
    if (!matchData?.id || !evtData.server_id) {
      console.warn('changeEvent skipped: no server_id, event was not synced');
      return;
    }
    try {
      const headers = await getAuthHeaders();
      await axios.post(BASE_URL.url + '/matches/changeEvent', {
        match_id: matchData.id,
        event_id: evtData.server_id,
        event_type: evtData.event_type,
        team_id: evtData.team_id,
        player_id: evtData.player_id,
        league_id: matchData.league_id,
        time: evtData.time,
        additional_player_id: evtData.additional_player_id || (evtData.assist_player_id || null),
        time_number: evtData.time_number,
        previous_event_type: previousEventType,
        previous_team_id: previousTeamId,
        additional_event_id: evtData.additional_event_id || null,
      }, { headers });
    } catch (err) {
      console.error('changeEvent error:', err);
      showNotif('Ошибка изменения события на сервере');
    }
  };

  // ═══ API: Fetch Events from Server ═══
  const fetchServerEvents = async () => {
    if (!matchData?.id) return [];
    try {
      const headers = await getAuthHeaders();
      const res = await axios.get(BASE_URL.url + '/matches/' + matchData.id + '/events', { headers });
      const raw = res.data?.data || res.data || [];
      return Array.isArray(raw) ? raw : [];
    } catch (err) {
      console.error('fetchServerEvents error:', err);
      return [];
    }
  };

  // ═══ API: Fetch Match Data from Server (to get status) ═══
  const fetchMatchFromServer = async () => {
    if (!matchData?.id) return null;
    try {
      const headers = await getAuthHeaders();
      const res = await axios.get(BASE_URL.url + '/matches/' + matchData.id, { headers });
      return res.data?.data || res.data || null;
    } catch (err) {
      console.error('fetchMatchFromServer error:', err);
      return null;
    }
  };

  // ═══ Convert server event to local format ═══
  const serverEventToLocal = (sEvt) => {
    const player = matchApplication ? matchApplication.find(p => p.id === sEvt.player_id) : null;
    const addPlayer = sEvt.additional_player_id ? (matchApplication ? matchApplication.find(p => p.id === sEvt.additional_player_id) : null) : null;
    // Parse time "HH:MM:SS" or "MM:SS"
    let minute = 0, second = 0;
    if (sEvt.time) {
      const parts = sEvt.time.split(':').map(Number);
      if (parts.length === 3) { minute = parts[1]; second = parts[2]; }
      else if (parts.length === 2) { minute = parts[0]; second = parts[1]; }
    }
    const eventType = sEvt.event_id; // In DB: event_id = event type code
    const isSystem = [9, 10, 11].includes(eventType);
    return {
      id: sEvt.id, // Use server ID as the local ID too
      server_id: sEvt.id, // Server DB record ID
      event_type: eventType,
      player_id: sEvt.player_id,
      player_name: player ? (player.family + ' ' + player.name) : (sEvt.player_family ? sEvt.player_family + ' ' + (sEvt.player_name || '') : null),
      player_number: player ? player.player_number : (sEvt.player_number || null),
      team_id: sEvt.team_id,
      team_name: sEvt.team_id === homeTeamId ? matchData?.home_team_name : (sEvt.team_id === guestTeamId ? matchData?.guest_team_name : null),
      minute, second,
      time: sEvt.time || toServerEventTime(minute, second),
      time_number: sEvt.time_number || (minute >= halfTime ? 2 : 1),
      assist_player_id: (eventType === 1 && sEvt.additional_player_id) ? sEvt.additional_player_id : null,
      assist_player_name: (eventType === 1 && addPlayer) ? (addPlayer.family + ' ' + addPlayer.name) : null,
      additional_player_id: (eventType === 15 && sEvt.additional_player_id) ? sEvt.additional_player_id : null,
      additional_player_name: (eventType === 15 && addPlayer) ? (addPlayer.family + ' ' + addPlayer.name) : null,
      additional_event_id: sEvt.additional_event_id || null,
      isSystem,
    };
  };

  // ═══ Reload events from server and sync local state ═══
  const syncEventsFromServer = async () => {
    const serverEvents = await fetchServerEvents();
    // Filter out assist events (event_id=2) - they are linked to goals
    const processed = [];
    serverEvents.forEach((sEvt) => {
      if (sEvt.event_id === 2) {
        // Link assist to previous event (goal)
        if (processed.length > 0) {
          processed[processed.length - 1].additional_event_id = sEvt.id;
        }
      } else {
        processed.push(serverEventToLocal(sEvt));
      }
    });
    setMatchEvents(processed);
  };

  // ═══ Restore match state from server ═══
  const restoreMatchFromServer = async () => {
    setIsRestoring(true);
    try {
      const serverMatch = await fetchMatchFromServer();
      if (!serverMatch) { setIsRestoring(false); return false; }

      const status = serverMatch.status;
      // status: 0=not started, 1=in progress, 2=ended, 3=halftime, 4=extra time
      if (!status || status === 0) { setIsRestoring(false); return false; }

      // Match is in progress or ended — restore state
      // Load events
      const serverEvents = await fetchServerEvents();
      const processedEvents = [];
      serverEvents.forEach((sEvt) => {
        if (sEvt.event_id === 2) {
          if (processedEvents.length > 0) {
            processedEvents[processedEvents.length - 1].additional_event_id = sEvt.id;
          }
        } else {
          processedEvents.push(serverEventToLocal(sEvt));
        }
      });
      setMatchEvents(processedEvents);

      // Set selected players from match application and build lineups
      if (matchApplication) {
        const sel = {};
        matchApplication.forEach(p => { sel[p.id] = !!p.active; });
        setSelectedPlayers(sel);

        // Build lineups from active players
        const homePlayers = matchApplication.filter(p => (p.team_id === homeTeamId || p.isDouble === homeTeamId) && p.active);
        const guestPlayers = matchApplication.filter(p => (p.team_id === guestTeamId || p.isDouble === guestTeamId) && p.active);

        // Find keepers
        const homeKeeper = homePlayers.find(p => p.keeper);
        const guestKeeper = guestPlayers.find(p => p.keeper);

        // Build lineup objects: slot 0 = keeper, rest = field players
        const buildLineup = (players, keeper) => {
          const lineup = {};
          let slot = 0;
          // Keeper first (slot 0)
          if (keeper) {
            lineup[slot] = keeper.id;
            slot++;
          }
          // Other active players
          players.forEach(p => {
            if (keeper && p.id === keeper.id) return;
            lineup[slot] = p.id;
            slot++;
          });
          return lineup;
        };

        const hLineup = buildLineup(homePlayers, homeKeeper);
        const gLineup = buildLineup(guestPlayers, guestKeeper);

        // Apply substitutions from events
        const applySubs = (lineup, teamId) => {
          const subs = processedEvents.filter(e => e.event_type === 15 && e.team_id === teamId && !e.isSystem);
          subs.forEach(sub => {
            const slotKey = Object.keys(lineup).find(k => lineup[k] === sub.player_id);
            if (slotKey !== undefined && sub.additional_player_id) {
              lineup[slotKey] = sub.additional_player_id;
            }
          });
          return lineup;
        };

        setHomeLineup(applySubs(hLineup, homeTeamId));
        setGuestLineup(applySubs(gLineup, guestTeamId));

        // Auto-detect format from number of active players per team
        const maxActive = Math.max(Object.keys(hLineup).length, Object.keys(gLineup).length);
        let detectedFormat = '';
        for (const fmt of MATCH_FORMATS) {
          const num = parseInt(fmt.split('x')[0]);
          if (num === maxActive) { detectedFormat = fmt; break; }
        }
        if (detectedFormat) {
          setMatchFormat(detectedFormat);
          const formations = FORMATIONS_BY_FORMAT[detectedFormat] || [];
          if (formations.length > 0) {
            setHomeFormation(formations[0]);
            setGuestFormation(formations[0]);
          }
        }
      }

      // Determine match status and timer
      if (status === 2 || status === 5) {
        setMatchStatus('ended');
        setPhase('ended');
        setDisplaySeconds(halfTime * 2 * 60);
      } else if (status === 3) {
        setMatchStatus('halftime');
        setPhase('live');
        setDisplaySeconds(halfTime * 60);
      } else if (status === 1 || status === 4) {
        const isSecondHalf = !!serverMatch.second_time_start;
        if (isSecondHalf) {
          setMatchStatus('half2');
          if (serverMatch.start_in) {
            const startMoment = new Date(serverMatch.start_in).getTime();
            const now = Date.now();
            const elapsedSec = Math.floor((now - startMoment) / 1000);
            setDisplaySeconds(Math.max(halfTime * 60, elapsedSec));
          } else {
            setDisplaySeconds(halfTime * 60);
          }
        } else {
          setMatchStatus('half1');
          if (serverMatch.start_in) {
            const startMoment = new Date(serverMatch.start_in).getTime();
            const now = Date.now();
            const elapsedSec = Math.floor((now - startMoment) / 1000);
            setDisplaySeconds(Math.max(0, elapsedSec));
          } else {
            setDisplaySeconds(0);
          }
        }
        setPhase('live');
      }

      setIsRestoring(false);
      return true;
    } catch (err) {
      console.error('restoreMatchFromServer error:', err);
      setIsRestoring(false);
      return false;
    }
  };

  // ═══ API: Set Application (lineup) ═══
  const callSetApplication = async (teamId) => {
    if (!matchData?.id) return;
    try {
      const headers = await getAuthHeaders();
      const teamPlayers = getTeamPlayers(teamId);
      const activeIds = teamPlayers.filter(p => selectedPlayers[p.id]).map(p => p.id);
      const inActiveIds = teamPlayers.filter(p => !selectedPlayers[p.id]).map(p => p.id);
      const doublesIds = teamPlayers.filter(p => p.isDouble).map(p => p.id);
      // Find goalkeeper: slot 0 in the corresponding lineup
      const lineup = teamId === homeTeamId ? homeLineup : guestLineup;
      const keeperId = lineup[0] || false;
      await axios.post(BASE_URL.url + '/matches/setApplication', {
        match_id: matchData.id,
        team_id: teamId,
        activeIds,
        inActiveIds,
        doublesIds,
        keeperId,
        matchNumbers: [],
      }, { headers });
    } catch (err) {
      console.error('setApplication error:', err);
      showNotif('Ошибка сохранения заявки на сервер');
    }
  };

  const startFirstHalf = async () => {
    showNotif('Сохранение заявок...');
    await callSetApplication(homeTeamId);
    await callSetApplication(guestTeamId);
    showNotif('Запуск матча...');
    await callChangeState('start');
    setDisplaySeconds(0);
    setMatchStatus('half1');
    addSystemEvent(9, 0);
    showNotif('Матч начался! 1-й тайм');
  };

  const pauseTimer = () => {
    if (matchStatus === 'half1') setMatchStatus('paused1');
    else if (matchStatus === 'half2') setMatchStatus('paused2');
  };

  const resumeTimer = () => {
    if (matchStatus === 'paused1') setMatchStatus('half1');
    else if (matchStatus === 'paused2') setMatchStatus('half2');
  };

  const callHalftime = async () => {
    await callChangeState('timeout');
    addSystemEvent(10, displaySeconds);
    setMatchStatus('halftime');
    showNotif('Перерыв!');
  };

  const startSecondHalf = async () => {
    await callChangeState('continue');
    setDisplaySeconds(halfTime * 60);
    setMatchStatus('half2');
    showNotif('2-й тайм начался!');
  };

  const endMatch = async () => {
    await callChangeState('end');
    addSystemEvent(11, displaySeconds);
    setMatchStatus('ended');
    setPhase('ended');
    showNotif('Матч завершён!');
  };

  const startEditTimer = () => {
    if (['paused1', 'paused2', 'halftime', 'idle'].includes(matchStatus)) {
      setEditMin(String(currentMinute));
      setEditSec(String(currentSecond));
      setEditingTimer(true);
    }
  };

  const applyTimerEdit = () => {
    const m = parseInt(editMin) || 0;
    const s = parseInt(editSec) || 0;
    setDisplaySeconds(m * 60 + s);
    setEditingTimer(false);
  };

  // ═══ EVENT RECORDING ═══
  const openEventModal = (teamSide, presetType, presetPlayerId) => {
    setEditingEventId(null);
    setEventModalTeamSide(teamSide);
    setEventType(presetType || 1);
    // Keep the player if provided
    setEventPlayer(presetPlayerId ? String(presetPlayerId) : '');
    setEventAssist('');
    setEventSubPlayer('');
    setEventMinute(String(currentMinute));
    setEventSecond(String(currentSecond));
    setEventModalOpen(true);
    setShowMoreEvents(false);
  };

  const openEditEventModal = (evt) => {
    if (evt.isSystem) return;
    setEditingEventId(evt.id);
    const side = evt.team_id === homeTeamId ? 'home' : 'guest';
    setEventModalTeamSide(side);
    setEventType(evt.event_type);
    setEventPlayer(String(evt.player_id));
    setEventAssist(evt.assist_player_id ? String(evt.assist_player_id) : '');
    setEventSubPlayer(evt.additional_player_id ? String(evt.additional_player_id) : '');
    setEventMinute(String(evt.minute));
    setEventSecond(String(evt.second));
    setEventModalOpen(true);
    setShowMoreEvents(false);
  };

  const saveEvent = () => {
    if (!eventPlayer) return;
    const teamId = eventModalTeamSide === 'home' ? homeTeamId : guestTeamId;
    const player = matchApplication.find(p => p.id === parseInt(eventPlayer));
    if (!player) return;
    // For substitution: require a sub player to be selected
    if (parseInt(eventType) === 15 && !eventSubPlayer) return;

    const min = parseInt(eventMinute) || 0;
    const sec = parseInt(eventSecond) || 0;

    // Auto-convert 2nd ЖК: if player already has 1 yellow and event is ЖК (3), change to 2nd yellow (13)
    let finalEventType = parseInt(eventType);
    if (finalEventType === 3 && !editingEventId) {
      const existingYellows = getPlayerYellows(player.id);
      if (existingYellows >= 1) {
        finalEventType = 13; // 2nd yellow = auto red
      }
    }

    const evtData = {
      id: editingEventId || (Date.now() + Math.random()),
      event_type: finalEventType,
      player_id: player.id,
      player_name: player.family + ' ' + player.name,
      player_number: player.player_number,
      team_id: teamId,
      team_name: eventModalTeamSide === 'home' ? matchData.home_team_name : matchData.guest_team_name,
      minute: min, second: sec,
      time: toServerEventTime(min, sec),
      time_number: min >= halfTime ? 2 : 1,
      assist_player_id: null, assist_player_name: null,
      additional_player_id: null, additional_player_name: null,
      isSystem: false,
    };

    // Assist for goals
    if (LIVE_EVENT_TYPES[finalEventType]?.hasAssist && eventAssist) {
      const ap = matchApplication.find(p => p.id === parseInt(eventAssist));
      if (ap) {
        evtData.assist_player_id = ap.id;
        evtData.assist_player_name = ap.family + ' ' + ap.name;
      }
    }

    // Substitution: additional_player = player coming ON
    if (finalEventType === 15 && eventSubPlayer) {
      const sp = matchApplication.find(p => p.id === parseInt(eventSubPlayer));
      if (sp) {
        evtData.additional_player_id = sp.id;
        evtData.additional_player_name = sp.family + ' ' + sp.name;
      }
    }

    if (editingEventId) {
      const oldEvt = matchEvents.find(e => e.id === editingEventId);
      // Preserve server_id and additional_event_id from the old event
      if (oldEvt?.server_id) evtData.server_id = oldEvt.server_id;
      if (oldEvt?.additional_event_id) evtData.additional_event_id = oldEvt.additional_event_id;
      setMatchEvents(prev => prev.map(e => e.id === editingEventId ? evtData : e));
      showNotif('Событие обновлено');
      // Send change to server, then sync
      callChangeEvent(evtData, oldEvt?.event_type, oldEvt?.team_id).then(() => {
        syncEventsFromServer();
      });
    } else {
      setMatchEvents(prev => [...prev, evtData]);
      const evtInfo = LIVE_EVENT_TYPES[finalEventType];
      const msg = finalEventType === 13
        ? `🟨🟨 2-я ЖК → КК: ${player.family} ${player.name}`
        : `${evtInfo?.icon} ${evtInfo?.label}: ${player.family} ${player.name}`;
      showNotif(msg);

      // Send event to server, then reload events to get proper server IDs
      callSetEvent(evtData).then(() => {
        // Reload all events from server to get correct server_id values
        syncEventsFromServer();
      });

      // For substitution: swap player on field
      if (finalEventType === 15 && eventSubPlayer) {
        const side = eventModalTeamSide;
        const lineup = side === 'home' ? { ...homeLineup } : { ...guestLineup };
        // Find slot of player going OUT
        const slotKey = Object.keys(lineup).find(k => lineup[k] === player.id);
        if (slotKey !== undefined) {
          lineup[slotKey] = parseInt(eventSubPlayer);
          if (side === 'home') setHomeLineup(lineup);
          else setGuestLineup(lineup);
        }
      }
    }

    setEventModalOpen(false);
    setEditingEventId(null);
  };

  const removeEvent = async (eventId) => {
    const evt = matchEvents.find(e => e.id === eventId);
    if (!evt || evt.isSystem) return;
    // Remove from UI immediately (optimistic)
    setMatchEvents(prev => prev.filter(e => e.id !== eventId));
    // Remove from server
    const success = await callRemoveEvent(evt);
    if (success) {
      // Reload events from server to stay in sync
      await syncEventsFromServer();
    } else {
      // Restore event in UI if server deletion failed
      setMatchEvents(prev => [...prev, evt]);
    }
  };

  const handleFieldPlayerClick = (side, playerId) => {
    if (!isMatchActive()) return;
    if (isPlayerSubbedOut(playerId)) {
      showNotif('Игрок уже заменён');
      return;
    }
    openEventModal(side, isPlayerBanned(playerId) ? 15 : 1, playerId);
  };

  const getPlayerCardIndicators = (playerId) => {
    const events = matchEvents.filter(e => e.player_id === playerId && !e.isSystem);
    let yellow = 0, red = 0, goals = 0;
    events.forEach(e => {
      if (e.event_type === 3) yellow++;
      if (e.event_type === 13) { yellow++; red++; } // 2nd yellow = YC + RC
      if (e.event_type === 4) red++;
      if ([1, 6, 16, 18].includes(e.event_type)) goals++;
    });
    return { yellow, red, goals, banned: red > 0 };
  };

  // ═══ MATCH FINISH ═══
  const applyResults = () => {
    const playerStats = {};
    const playerEvents = matchEvents.filter(e => !e.isSystem);

    playerEvents.forEach(e => {
      const t = LIVE_EVENT_TYPES[e.event_type];
      if (!t) return;
      if (t.stat && e.player_id) {
        if (!playerStats[e.player_id]) playerStats[e.player_id] = { goals: 0, assists: 0, autogoals: 0, yellow_cards: 0, red_cards: 0 };
        playerStats[e.player_id][t.stat] += 1;
      }
      if (e.assist_player_id) {
        if (!playerStats[e.assist_player_id]) playerStats[e.assist_player_id] = { goals: 0, assists: 0, autogoals: 0, yellow_cards: 0, red_cards: 0 };
        playerStats[e.assist_player_id].assists += 1;
      }
    });

    const activePlayers = Object.entries(selectedPlayers).filter(([, v]) => v).map(([id]) => parseInt(id));

    const manualEvts = playerEvents.map(e => ({
      id: e.id, event_type: e.event_type,
      player_id: e.player_id, player_name: e.player_name, player_number: e.player_number,
      team_id: e.team_id, team_name: e.team_name,
      minute: e.minute, second: e.second, time: e.time, time_number: e.time_number,
      assist_player_id: e.assist_player_id, assist_player_name: e.assist_player_name,
    }));

    if (onMatchFinish) {
      onMatchFinish({ score, manualEvents: manualEvts, playerStats, activePlayers });
    } else {
      closeModal();
    }
  };

  // ═══════════════════════════════════════
  // RENDER: ROSTER PHASE
  // ═══════════════════════════════════════

  const renderTeamRoster = (teamId, teamName, teamImage) => {
    const players = getTeamPlayers(teamId);
    const count = getSelectedCount(teamId);
    return (
      <div className="olm-roster-team">
        <div className="olm-roster-team-header">
          <img src={teamLogo(teamImage)} alt="" className="olm-team-logo" />
          <span className="olm-roster-team-name">{teamName}</span>
          <div className="olm-roster-actions">
            <span className="olm-action-link" onClick={() => selectAll(teamId)}>
              {players.every(p => selectedPlayers[p.id]) ? 'Снять всех' : 'Выбрать всех'}
            </span>
          </div>
        </div>
        <div className="olm-roster-list">
          {players.map(p => (
            <div
              key={p.id}
              className={`olm-roster-item ${selectedPlayers[p.id] ? 'olm-roster-item-selected' : ''}`}
              onClick={() => togglePlayer(p.id)}
            >
              <span className="olm-roster-number">{p.player_number || '—'}</span>
              <span className="olm-roster-name">{p.family} {p.name} {p.isDouble === teamId ? '(Д)' : ''}</span>
              {p.position_id && <span className="olm-roster-pos">{POSITION_LABELS[p.position_id]}</span>}
              <span className="olm-roster-check">{selectedPlayers[p.id] ? '✓' : ''}</span>
            </div>
          ))}
        </div>
        <div className="olm-roster-count">Выбрано: {count}</div>
      </div>
    );
  };

  const renderRosterPhase = () => (
    <div>
      <h3 className="olm-phase-title">Выбор заявки на матч</h3>
      <p className="olm-phase-subtitle">Отметьте игроков, участвующих в матче</p>
      <div className="olm-roster-columns">
        {renderTeamRoster(homeTeamId, matchData.home_team_name, matchData.home_team_image)}
        {renderTeamRoster(guestTeamId, matchData.guest_team_name, matchData.guest_team_image)}
      </div>
      <div className="olm-phase-actions">
        <div className="olm-btn olm-btn-secondary" onClick={closeModal}>Отмена</div>
        <div className="olm-btn olm-btn-start" onClick={goToLineup}>Далее →</div>
      </div>
    </div>
  );

  // ═══════════════════════════════════════
  // RENDER: LINEUP PHASE (with player assignment)
  // ═══════════════════════════════════════

  const renderLineupField = (side, teamName, formation) => {
    const lineup = getLineup(side);
    const teamColor = side === 'home' ? homeColor : guestColor;
    const bench = getBenchPlayers(side);

    const handleDragOverSlot = (e) => e.preventDefault();

    const handleLineupDrop = (slotIndex) => {
      if (!draggingPlayer || draggingPlayer.fromSide !== side) {
        setDragOverTarget(null);
        return;
      }
      assignPlayerToSlot(side, slotIndex, draggingPlayer.id);
      setDraggingPlayer(null);
      setDragOverTarget(null);
    };

    if (!formation) {
      return (
        <div className="olm-lineup-team-body">
          <div className={`olm-lineup-player-panel ${side === 'guest' ? 'olm-lineup-panel-right' : ''}`}>
            <div className="olm-lineup-panel-header">Игроки ({bench.length})</div>
            <div className="olm-lineup-panel-list">
              {bench.map(p => {
                const posLabel = POSITION_LABELS[p.position_id] || '';
                return (
                  <div key={p.id} className="olm-lineup-player-card">
                    <div className="olm-lineup-card-number" style={{ background: teamColor }}>{p.player_number || '—'}</div>
                    <div className="olm-lineup-card-info">
                      <span className="olm-lineup-card-name">{p.family} {p.name}</span>
                      {posLabel && <span className="olm-lineup-card-pos">{posLabel}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="olm-lineup-field-col">
            <div className="olm-field olm-field-lineup">
              <span style={{ color: 'rgba(255,255,255,0.5)', position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', fontSize: 13 }}>Выберите схему</span>
            </div>
          </div>
        </div>
      );
    }

    const positions = generateFieldPositions(formation);

    const playerListPanel = (
      <div className={`olm-lineup-player-panel ${side === 'guest' ? 'olm-lineup-panel-right' : ''}`}>
        <div className="olm-lineup-panel-header">
          {pickingSlot && pickingSlot.teamSide === side
            ? <span className="olm-lineup-panel-picking">📍 Выберите → {pickingSlot.slotIndex === 0 ? 'GK' : `поз. ${pickingSlot.slotIndex}`}</span>
            : <span>Игроки ({bench.length})</span>
          }
        </div>
        <div className="olm-lineup-panel-list">
          {bench.map(p => {
            const posLabel = POSITION_LABELS[p.position_id] || '';
            const isPicking = pickingSlot && pickingSlot.teamSide === side;
            return (
              <div
                key={p.id}
                className={`olm-lineup-player-card ${isPicking ? 'olm-lineup-player-card-active' : ''}`}
                draggable
                onDragStart={() => setDraggingPlayer({ id: p.id, fromSide: side, fromBench: true })}
                onDragEnd={() => { setDraggingPlayer(null); setDragOverTarget(null); }}
                onClick={() => {
                  if (isPicking) {
                    assignPlayerToSlot(side, pickingSlot.slotIndex, p.id);
                  }
                }}
                style={{ borderLeftColor: isPicking ? teamColor : 'transparent' }}
              >
                <div className="olm-lineup-card-number" style={{ background: teamColor }}>{p.player_number || '—'}</div>
                <div className="olm-lineup-card-info">
                  <span className="olm-lineup-card-name">{p.family} {p.name}</span>
                  {posLabel && <span className="olm-lineup-card-pos">{posLabel}</span>}
                </div>
              </div>
            );
          })}
          {bench.length === 0 && (
            <div className="olm-lineup-panel-empty">Все игроки на поле ✓</div>
          )}
        </div>
      </div>
    );

    const fieldEl = (
      <div className="olm-lineup-field-col">
        <div className="olm-lineup-actions-row">
          <span className="olm-action-link" onClick={() => autoFillLineup(side)}>Авто</span>
          <span className="olm-action-link" onClick={() => { setLineupSide(side, {}); setPickingSlot(null); }}>Очистить</span>
        </div>
        <div className="olm-field olm-field-lineup" onDragOver={handleDragOverSlot}>
          <div className="olm-field-half-line" />
          <div className="olm-field-center-circle" />
          <div className="olm-field-penalty-area olm-field-penalty-bottom" />
          <div className="olm-field-penalty-area olm-field-penalty-top" />
          {positions.map((pos, i) => {
            const playerId = lineup[i];
            const player = playerId ? findPlayerById(playerId) : null;
            const isPickTarget = pickingSlot && pickingSlot.teamSide === side && pickingSlot.slotIndex === i;
            const isDragOver = dragOverTarget && dragOverTarget.side === side && dragOverTarget.slotIndex === i;
            return (
              <div
                key={i}
                className={`olm-field-slot ${player ? 'olm-field-slot-filled' : 'olm-field-slot-empty'} ${isPickTarget ? 'olm-field-slot-picking' : ''} ${isDragOver && !player ? 'olm-field-slot-drag-over' : ''}`}
                style={{ top: `${pos.top}%`, left: `${pos.left}%`, background: player ? (i === 0 ? '#f39c12' : teamColor) : 'transparent' }}
                onClick={() => handleSlotClick(side, i)}
                onDragOver={(e) => { e.preventDefault(); setDragOverTarget({ side, slotIndex: i }); }}
                onDragLeave={() => setDragOverTarget(null)}
                onDrop={() => handleLineupDrop(i)}
                draggable={!!player}
                onDragStart={() => player && setDraggingPlayer({ id: player.id, fromSide: side, fromBench: false })}
              >
                {player ? (
                  <>
                    <span className="olm-field-player-pos">{getPositionShort(player, i)}</span>
                    <span className="olm-field-player-number">{player.player_number || ''}</span>
                    <span className="olm-field-player-name">{player.family}</span>
                  </>
                ) : (
                  <span className="olm-field-slot-label">{i === 0 ? 'GK' : i}</span>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );

    return (
      <div className={`olm-lineup-team-body ${side === 'guest' ? 'olm-lineup-body-reverse' : ''}`}>
        {playerListPanel}
        {fieldEl}
      </div>
    );
  };

  const renderLineupPhase = () => {
    const formations = matchFormat ? (FORMATIONS_BY_FORMAT[matchFormat] || []) : [];
    return (
      <div>
        <h3 className="olm-phase-title">Расстановка и стартовый состав</h3>
        <p className="olm-phase-subtitle">Выберите формат, схему и расставьте игроков. Нажмите на позицию → затем на игрока.</p>

        {/* Jersey color pickers */}
        <div className="olm-color-pickers">
          <div className="olm-color-picker-item">
            <label>Цвет формы {matchData.home_team_name}:</label>
            <input type="color" value={homeColor} onChange={e => setHomeColor(e.target.value)} />
          </div>
          <div className="olm-color-picker-item">
            <label>Цвет формы {matchData.guest_team_name}:</label>
            <input type="color" value={guestColor} onChange={e => setGuestColor(e.target.value)} />
          </div>
        </div>

        {/* Format selector */}
        <div className="olm-format-section">
          <label className="olm-format-label-text">Формат матча:</label>
          <div className="olm-format-row">
            {MATCH_FORMATS.map(fmt => (
              <div
                key={fmt}
                className={`olm-btn ${matchFormat === fmt ? 'olm-btn-primary' : 'olm-btn-secondary'}`}
                style={{ padding: '6px 14px', fontSize: 13 }}
                onClick={() => onFormatChange(fmt)}
              >
                {fmt}
              </div>
            ))}
          </div>
        </div>

        {matchFormat && (
          <div className="olm-lineup-columns">
            {['home', 'guest'].map(side => {
              const teamName = side === 'home' ? matchData.home_team_name : matchData.guest_team_name;
              const formation = getFormation(side);
              return (
                <div key={side} className="olm-lineup-team">
                  <div className="olm-lineup-team-name">{teamName}</div>
                  <div className="olm-format-row" style={{ marginBottom: 8, justifyContent: 'center' }}>
                    {formations.map(f => (
                      <div
                        key={f}
                        className={`olm-btn ${formation === f ? 'olm-btn-primary' : 'olm-btn-secondary'}`}
                        style={{ padding: '5px 10px', fontSize: 12 }}
                        onClick={() => onFormationChange(side, f)}
                      >
                        {f}
                      </div>
                    ))}
                  </div>
                  {renderLineupField(side, teamName, formation)}
                </div>
              );
            })}
          </div>
        )}

        <div className="olm-phase-actions">
          <div className="olm-btn olm-btn-secondary" onClick={() => { setPhase('roster'); setPickingSlot(null); }}>← Назад</div>
          <div className="olm-btn olm-btn-start" onClick={goToLive}>Начать матч →</div>
        </div>
      </div>
    );
  };

  // ═══════════════════════════════════════
  // RENDER: LIVE PHASE
  // ═══════════════════════════════════════

  const renderTimerControls = () => {
    switch (matchStatus) {
      case 'idle':
        return <div className="olm-btn olm-btn-start" onClick={startFirstHalf}>▶ Начать 1-й тайм</div>;
      case 'half1':
        return (
          <>
            <div className="olm-btn olm-btn-warning" onClick={pauseTimer}>⏸ Пауза</div>
            <div className="olm-btn olm-btn-primary" onClick={callHalftime}>⏱ Перерыв</div>
          </>
        );
      case 'paused1':
        return (
          <>
            <div className="olm-btn olm-btn-start" onClick={resumeTimer}>▶ Продолжить</div>
            <div className="olm-btn olm-btn-primary" onClick={callHalftime}>⏱ Перерыв</div>
          </>
        );
      case 'halftime':
        return <div className="olm-btn olm-btn-start" onClick={startSecondHalf}>▶ Начать 2-й тайм</div>;
      case 'half2':
        return (
          <>
            <div className="olm-btn olm-btn-warning" onClick={pauseTimer}>⏸ Пауза</div>
            <div className="olm-btn olm-btn-danger" onClick={endMatch}>🏁 Завершить матч</div>
          </>
        );
      case 'paused2':
        return (
          <>
            <div className="olm-btn olm-btn-start" onClick={resumeTimer}>▶ Продолжить</div>
            <div className="olm-btn olm-btn-danger" onClick={endMatch}>🏁 Завершить матч</div>
          </>
        );
      default:
        return null;
    }
  };

  const renderEventButtons = (teamSide) => {
    if (!isMatchActive()) return null;
    return (
      <div className="olm-field-event-buttons">
        {QUICK_EVENTS.map(evtId => {
          const evt = LIVE_EVENT_TYPES[evtId];
          return (
            <div key={evtId} className="olm-btn olm-btn-event-sm" onClick={() => openEventModal(teamSide, evtId)}>
              {evt.icon}
            </div>
          );
        })}
        <div className="olm-btn olm-btn-event-sm" style={{ background: '#34495e' }} onClick={() => { setEventModalTeamSide(teamSide); setShowMoreEvents(!showMoreEvents); }}>
          ▼
        </div>
      </div>
    );
  };

  const renderLiveField = (side) => {
    const formation = side === 'home' ? homeFormation : guestFormation;
    const lineup = side === 'home' ? homeLineup : guestLineup;
    const teamName = side === 'home' ? matchData.home_team_name : matchData.guest_team_name;
    const teamColor = side === 'home' ? homeColor : guestColor;
    const bench = getLiveBenchPlayers(side);

    if (!formation) return null;
    const positions = generateFieldPositions(formation);

    const handleDragOver = (e) => e.preventDefault();

    const handleDropOnSlot = (slotIndex) => {
      if (!draggingPlayer) return;
      const dragId = draggingPlayer.id;
      const dragFromSide = draggingPlayer.fromSide;
      if (dragFromSide !== side) return;
      const currentLineup = { ...(side === 'home' ? homeLineup : guestLineup) };
      const existingPlayerId = currentLineup[slotIndex];

      if (existingPlayerId && existingPlayerId !== dragId) {
        // Dragging bench player onto occupied slot = substitution
        if (draggingPlayer.fromBench) {
          // Create substitution event: existingPlayer OUT, dragId IN
          const outPlayer = findPlayerById(existingPlayerId);
          const inPlayer = findPlayerById(dragId);
          if (outPlayer && inPlayer && isMatchActive()) {
            const tId = side === 'home' ? homeTeamId : guestTeamId;
            const min = currentMinute;
            const sec = currentSecond;
            const subEvent = {
              id: Date.now() + Math.random(),
              event_type: 15,
              player_id: outPlayer.id,
              player_name: outPlayer.family + ' ' + outPlayer.name,
              player_number: outPlayer.player_number,
              team_id: tId,
              team_name: side === 'home' ? matchData.home_team_name : matchData.guest_team_name,
              minute: min, second: sec,
              time: toServerEventTime(min, sec),
              time_number: min >= halfTime ? 2 : 1,
              assist_player_id: null, assist_player_name: null,
              additional_player_id: inPlayer.id,
              additional_player_name: inPlayer.family + ' ' + inPlayer.name,
              isSystem: false,
            };
            setMatchEvents(prev => [...prev, subEvent]);
            currentLineup[slotIndex] = dragId;
            if (side === 'home') setHomeLineup(currentLineup);
            else setGuestLineup(currentLineup);
            showNotif(`🔄 Замена: ${outPlayer.family} → ${inPlayer.family}`);
            // Send substitution to server, then reload events
            callSetEvent(subEvent).then(() => {
              syncEventsFromServer();
            });
          }
        }
      } else if (!existingPlayerId) {
        // Drag onto empty slot
        currentLineup[slotIndex] = dragId;
        // Remove from old slot if moving
        if (!draggingPlayer.fromBench) {
          Object.keys(currentLineup).forEach(k => {
            if (parseInt(k) !== slotIndex && currentLineup[k] === dragId) delete currentLineup[k];
          });
        }
        if (side === 'home') setHomeLineup(currentLineup);
        else setGuestLineup(currentLineup);
      }
      setDraggingPlayer(null);
    };

    return (
      <div className="olm-live-field-wrapper">
        <div className="olm-live-field-header">{teamName}</div>
        <div className="olm-field olm-field-live" onDragOver={handleDragOver}>
          <div className="olm-field-half-line" />
          <div className="olm-field-center-circle" />
          <div className="olm-field-penalty-area olm-field-penalty-bottom" />
          <div className="olm-field-penalty-area olm-field-penalty-top" />
          {positions.map((pos, i) => {
            const playerId = lineup[i];
            const player = playerId ? findPlayerById(playerId) : null;
            const indicators = playerId ? getPlayerCardIndicators(playerId) : { yellow: 0, red: 0, goals: 0, banned: false };
            const subbedOut = playerId && isPlayerSubbedOut(playerId);
            const redCarded = indicators.banned;
            const inactive = subbedOut;
            const clickable = isMatchActive() && player && !subbedOut;

            return (
              <div
                key={i}
                className={`olm-field-slot ${player ? 'olm-field-slot-filled' : 'olm-field-slot-empty'} ${clickable ? 'olm-field-slot-clickable' : ''} ${inactive ? 'olm-field-slot-banned' : ''} ${redCarded && !subbedOut ? 'olm-field-slot-red-carded' : ''}`}
                style={{
                  top: `${pos.top}%`, left: `${pos.left}%`,
                  background: player ? (inactive ? '#95a5a6' : redCarded ? '#c0392b' : (i === 0 ? '#f39c12' : teamColor)) : 'transparent',
                  cursor: clickable ? 'pointer' : 'default',
                  opacity: inactive ? 0.4 : 1,
                }}
                draggable={!!player && !inactive}
                onDragStart={() => player && !inactive && setDraggingPlayer({ id: player.id, fromSide: side, fromBench: false })}
                onDragOver={handleDragOver}
                onDrop={() => handleDropOnSlot(i)}
                onClick={() => { if (clickable) handleFieldPlayerClick(side, player.id); }}
              >
                {player ? (
                  <>
                    <span className="olm-field-player-pos">{getPositionShort(player, i)}</span>
                    <span className="olm-field-player-number">{player.player_number || ''}</span>
                    <span className={`olm-field-player-name ${redCarded && !subbedOut ? 'olm-field-player-name-banned' : ''}`}>{player.family}</span>
                    {indicators.yellow > 0 && <span className="olm-card-indicator olm-card-yellow">🟨</span>}
                    {indicators.red > 0 && <span className="olm-card-indicator olm-card-red">🟥</span>}
                    {indicators.goals > 0 && <span className="olm-goal-indicator">{'⚽'.repeat(indicators.goals)}</span>}
                  </>
                ) : (
                  <span className="olm-field-slot-label">{i === 0 ? 'GK' : ''}</span>
                )}
              </div>
            );
          })}
        </div>
        {renderEventButtons(side)}

        {/* Live bench (for substitutions) */}
        {bench.length > 0 && (
          <div className="olm-bench olm-bench-live">
            <div className="olm-bench-title">Скамейка ({bench.length}) — перетащите на поле для замены</div>
            <div className="olm-bench-list">
              {bench.map(p => (
                <div
                  key={p.id}
                  className="olm-player-badge"
                  style={{ background: teamColor, cursor: 'grab' }}
                  draggable
                  onDragStart={() => setDraggingPlayer({ id: p.id, fromSide: side, fromBench: true })}
                >
                  <span className="olm-player-badge-number">{p.player_number || '—'}</span>
                  <span className="olm-player-badge-name">{p.family}</span>
                  {p.position_id && <span className="olm-player-badge-pos">{POSITION_LABELS[p.position_id]}</span>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderLivePhase = () => (
    <div className="olm-live-phase">
      {/* Timer + Scoreboard */}
      <div className="olm-match-control">
        <div className="olm-timer-row">
          <div className="olm-timer-display">
            <span className="olm-timer-status">{statusLabel()}</span>
            {editingTimer ? (
              <div className="olm-timer-edit-row">
                <input
                  type="number" className="olm-timer-edit-input" value={editMin}
                  onChange={e => setEditMin(e.target.value)} min="0"
                />
                <span className="olm-timer-time" style={{ fontSize: 32 }}>:</span>
                <input
                  type="number" className="olm-timer-edit-input" value={editSec}
                  onChange={e => setEditSec(e.target.value)} min="0" max="59"
                />
                <div className="olm-btn olm-btn-primary" style={{ padding: '6px 14px', fontSize: 13, marginLeft: 8 }} onClick={applyTimerEdit}>OK</div>
                <div className="olm-btn olm-btn-secondary" style={{ padding: '6px 14px', fontSize: 13, marginLeft: 4 }} onClick={() => setEditingTimer(false)}>✕</div>
              </div>
            ) : (
              <span className="olm-timer-time" onClick={startEditTimer} style={{ cursor: ['paused1','paused2','halftime','idle'].includes(matchStatus) ? 'pointer' : 'default' }}>
                {formatTime(displaySeconds)}
              </span>
            )}
          </div>
        </div>
        {/* Compact scoreboard */}
        <div className="olm-scoreboard-inline">
          <img src={teamLogo(matchData.home_team_image)} alt="" className="olm-scoreboard-logo-sm" />
          <span className="olm-scoreboard-name-sm">{matchData.home_team_name}</span>
          <span className="olm-score-inline">{score.home} : {score.guest}</span>
          <span className="olm-scoreboard-name-sm">{matchData.guest_team_name}</span>
          <img src={teamLogo(matchData.guest_team_image)} alt="" className="olm-scoreboard-logo-sm" />
        </div>
        <div className="olm-control-buttons">
          {renderTimerControls()}
        </div>
        {/* Jersey color pickers */}
        <div className="olm-color-pickers olm-color-pickers-live">
          <div className="olm-color-picker-item">
            <label>{matchData.home_team_name}:</label>
            <input type="color" value={homeColor} onChange={e => setHomeColor(e.target.value)} />
          </div>
          <div className="olm-color-picker-item">
            <label>{matchData.guest_team_name}:</label>
            <input type="color" value={guestColor} onChange={e => setGuestColor(e.target.value)} />
          </div>
        </div>
      </div>

      {/* Two fields side by side */}
      <div className="olm-live-fields-row">
        {renderLiveField('home')}
        {renderLiveField('guest')}
      </div>

      {/* More events dropdown */}
      {showMoreEvents && (
        <div className="olm-more-events-row">
          {MORE_EVENTS.map(evtId => {
            const evt = LIVE_EVENT_TYPES[evtId];
            return (
              <div key={evtId} className="olm-btn olm-btn-event-sm" onClick={() => openEventModal(eventModalTeamSide, evtId)}>
                {evt.icon} {evt.label}
              </div>
            );
          })}
        </div>
      )}

      {/* Event History */}
      <div className="olm-event-history" style={{ maxWidth: '100%' }}>
        <div className="olm-event-history-title">События матча ({matchEvents.filter(e => !e.isSystem).length})</div>
        {matchEvents.length === 0 ? (
          <div className="olm-event-empty">Нет событий</div>
        ) : (
          <div className="olm-event-list">
            {[...matchEvents].sort((a, b) => a.minute - b.minute || a.second - b.second).map(e => {
              const evtType = LIVE_EVENT_TYPES[e.event_type];
              const sysLabel = SYSTEM_EVENT_LABELS[e.event_type];
              return (
                <div
                  key={e.id}
                  className={`olm-event-item ${e.team_id === homeTeamId ? 'olm-event-home' : e.team_id === guestTeamId ? 'olm-event-guest' : ''} ${!e.isSystem ? 'olm-event-item-editable' : ''}`}
                  onClick={() => { if (!e.isSystem) openEditEventModal(e); }}
                >
                  <span className="olm-event-time">{e.minute}'{e.second > 0 ? String(e.second).padStart(2, '0') : ''}</span>
                  <span className="olm-event-icon">{sysLabel ? '' : evtType?.icon}</span>
                  <div className="olm-event-info">
                    {sysLabel ? (
                      <span className="olm-event-label">{sysLabel}</span>
                    ) : (
                      <>
                        <span className="olm-event-label">{evtType?.label}</span>
                        <span className="olm-event-player">{e.player_name}</span>
                        {e.assist_player_name && <span className="olm-event-assist">пас: {e.assist_player_name}</span>}
                        {e.event_type === 15 && e.additional_player_name && <span className="olm-event-assist">➜ {e.additional_player_name}</span>}
                        <span className="olm-event-team">{e.team_name}</span>
                      </>
                    )}
                  </div>
                  {!e.isSystem && (
                    <span className="olm-event-remove" title="Удалить" onClick={(ev) => { ev.stopPropagation(); removeEvent(e.id); }}>✕</span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  // ═══════════════════════════════════════
  // RENDER: ENDED PHASE
  // ═══════════════════════════════════════

  const renderEndedPhase = () => {
    const playerEvents = matchEvents.filter(e => !e.isSystem);
    const playerStats = {};
    playerEvents.forEach(e => {
      const t = LIVE_EVENT_TYPES[e.event_type];
      if (!t) return;
      if (t.stat && e.player_id) {
        if (!playerStats[e.player_id]) playerStats[e.player_id] = { goals: 0, assists: 0, autogoals: 0, yellow_cards: 0, red_cards: 0 };
        playerStats[e.player_id][t.stat] += 1;
      }
      if (e.assist_player_id) {
        if (!playerStats[e.assist_player_id]) playerStats[e.assist_player_id] = { goals: 0, assists: 0, autogoals: 0, yellow_cards: 0, red_cards: 0 };
        playerStats[e.assist_player_id].assists += 1;
      }
    });

    return (
      <div className="olm-phase-ended">
        <div className="olm-ended-banner">
          <h2>Матч завершён</h2>
          <div style={{ fontSize: 36, fontWeight: 800, marginTop: 10 }}>
            {matchData.home_team_name} {score.home} : {score.guest} {matchData.guest_team_name}
          </div>
        </div>

        {/* Player stats summary */}
        <div className="olm-roster-columns" style={{ marginBottom: 16 }}>
          {[homeTeamId, guestTeamId].map(teamId => {
            const teamName = teamId === homeTeamId ? matchData.home_team_name : matchData.guest_team_name;
            const players = getSelectedTeamPlayers(teamId);
            return (
              <div key={teamId} className="olm-roster-team" style={{ maxHeight: 'none' }}>
                <div className="olm-roster-team-name" style={{ marginBottom: 8, fontWeight: 700 }}>{teamName}</div>
                <table style={{ width: '100%', fontSize: 13, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #ecf0f1' }}>
                      <th style={{ textAlign: 'left', padding: '4px 6px' }}>Игрок</th>
                      <th style={{ padding: '4px 4px', width: 30 }}>⚽</th>
                      <th style={{ padding: '4px 4px', width: 30 }}>🅰️</th>
                      <th style={{ padding: '4px 4px', width: 30 }}>АГ</th>
                      <th style={{ padding: '4px 4px', width: 30 }}>🟨</th>
                      <th style={{ padding: '4px 4px', width: 30 }}>🟥</th>
                    </tr>
                  </thead>
                  <tbody>
                    {players.map(p => {
                      const s = playerStats[p.id] || {};
                      const hasStats = (s.goals || s.assists || s.autogoals || s.yellow_cards || s.red_cards);
                      return (
                        <tr key={p.id} style={{ borderBottom: '1px solid #f0f0f0', background: hasStats ? '#f8fff8' : 'transparent' }}>
                          <td style={{ padding: '4px 6px' }}>{p.player_number} {p.family} {p.name}</td>
                          <td style={{ textAlign: 'center' }}>{s.goals || ''}</td>
                          <td style={{ textAlign: 'center' }}>{s.assists || ''}</td>
                          <td style={{ textAlign: 'center' }}>{s.autogoals || ''}</td>
                          <td style={{ textAlign: 'center' }}>{s.yellow_cards || ''}</td>
                          <td style={{ textAlign: 'center' }}>{s.red_cards || ''}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>

        {/* Event log */}
        <div className="olm-event-history" style={{ maxWidth: '100%', maxHeight: 300 }}>
          <div className="olm-event-history-title">Все события ({matchEvents.length})</div>
          <div className="olm-event-list">
            {[...matchEvents].sort((a, b) => a.minute - b.minute || a.second - b.second).map(e => {
              const evtType = LIVE_EVENT_TYPES[e.event_type];
              const sysLabel = SYSTEM_EVENT_LABELS[e.event_type];
              return (
                <div key={e.id} className={`olm-event-item ${e.team_id === homeTeamId ? 'olm-event-home' : e.team_id === guestTeamId ? 'olm-event-guest' : ''}`}>
                  <span className="olm-event-time">{e.minute}'</span>
                  <span className="olm-event-icon">{sysLabel ? '' : evtType?.icon}</span>
                  <div className="olm-event-info">
                    {sysLabel ? (
                      <span className="olm-event-label">{sysLabel}</span>
                    ) : (
                      <>
                        <span className="olm-event-label">{evtType?.label}</span>
                        <span className="olm-event-player">{e.player_name}</span>
                        {e.assist_player_name && <span className="olm-event-assist">пас: {e.assist_player_name}</span>}
                        {e.event_type === 15 && e.additional_player_name && <span className="olm-event-assist">➜ {e.additional_player_name}</span>}
                        <span className="olm-event-team">{e.team_name}</span>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="olm-phase-actions">
          <div className="olm-btn olm-btn-secondary" onClick={closeModal}>Закрыть без сохранения</div>
          <div className="olm-btn olm-btn-start" onClick={applyResults}>✓ Применить результаты</div>
        </div>
      </div>
    );
  };

  // ═══════════════════════════════════════
  // RENDER: EVENT MODAL
  // ═══════════════════════════════════════

  const renderEventModal = () => {
    const teamId = eventModalTeamSide === 'home' ? homeTeamId : guestTeamId;
    const teamPlayers = getSelectedTeamPlayers(teamId);
    const evtTypeInfo = LIVE_EVENT_TYPES[eventType];
    const showAssist = evtTypeInfo?.hasAssist;
    const isSub = evtTypeInfo?.isSub;
    const assistPlayers = showAssist ? teamPlayers.filter(p => p.id !== parseInt(eventPlayer)) : [];
    const benchForSub = isSub ? getLiveBenchPlayers(eventModalTeamSide) : [];
    const isEditing = !!editingEventId;

    return (
      <div className="olm-event-modal-overlay" onClick={() => { setEventModalOpen(false); setEditingEventId(null); }}>
        <div className="olm-event-modal" onClick={e => e.stopPropagation()}>
          <div className="olm-event-modal-header">
            <span>{evtTypeInfo?.icon} {isEditing ? 'Редактировать событие' : 'Добавить событие'} — {eventModalTeamSide === 'home' ? matchData.home_team_name : matchData.guest_team_name}</span>
            <span className="olm-event-modal-time">{formatTime(displaySeconds)}</span>
          </div>
          <div className="olm-event-modal-body">
            {/* Event type */}
            <div className="olm-event-form-row">
              <label>Тип события</label>
              <select className="olm-select" value={eventType} onChange={e => { setEventType(parseInt(e.target.value)); setEventAssist(''); setEventSubPlayer(''); }}>
                {Object.entries(LIVE_EVENT_TYPES).map(([id, t]) => (
                  <option key={id} value={id}>{t.icon} {t.label}</option>
                ))}
              </select>
            </div>

            {/* Team side tabs */}
            <div className="olm-event-form-row">
              <label>Команда</label>
              <div style={{ display: 'flex', gap: 6 }}>
                <div
                  className={`olm-btn ${eventModalTeamSide === 'home' ? 'olm-btn-primary' : 'olm-btn-secondary'}`}
                  style={{ padding: '5px 14px', fontSize: 13, flex: 1 }}
                  onClick={() => { setEventModalTeamSide('home'); setEventPlayer(''); setEventAssist(''); }}
                >
                  {matchData.home_team_name}
                </div>
                <div
                  className={`olm-btn ${eventModalTeamSide === 'guest' ? 'olm-btn-primary' : 'olm-btn-secondary'}`}
                  style={{ padding: '5px 14px', fontSize: 13, flex: 1 }}
                  onClick={() => { setEventModalTeamSide('guest'); setEventPlayer(''); setEventAssist(''); }}
                >
                  {matchData.guest_team_name}
                </div>
              </div>
            </div>

            {/* Player */}
            <div className="olm-event-form-row">
              <label>Игрок</label>
              <select className="olm-select" value={eventPlayer} onChange={e => setEventPlayer(e.target.value)}>
                <option value="">— Выберите игрока —</option>
                {teamPlayers.map(p => (
                  <option key={p.id} value={p.id}>#{p.player_number} {p.family} {p.name}</option>
                ))}
              </select>
            </div>

            {/* Assist */}
            {showAssist && (
              <div className="olm-event-form-row">
                <label>Голевая передача (опционально)</label>
                <select className="olm-select" value={eventAssist} onChange={e => setEventAssist(e.target.value)}>
                  <option value="">— Без ассиста —</option>
                  {assistPlayers.map(p => (
                    <option key={p.id} value={p.id}>#{p.player_number} {p.family} {p.name}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Substitution: player IN */}
            {isSub && (
              <div className="olm-event-form-row">
                <label>Замена: игрок на замену (входит)</label>
                <select className="olm-select" value={eventSubPlayer} onChange={e => setEventSubPlayer(e.target.value)}>
                  <option value="">— Выберите игрока со скамейки —</option>
                  {benchForSub.map(p => (
                    <option key={p.id} value={p.id}>#{p.player_number} {p.family} {p.name}</option>
                  ))}
                </select>
                {eventPlayer && eventSubPlayer && (
                  <div style={{ marginTop: 6, fontSize: 13, color: '#7f8c8d' }}>
                    🔄 {findPlayerById(parseInt(eventPlayer))?.family} ➜ {findPlayerById(parseInt(eventSubPlayer))?.family}
                  </div>
                )}
              </div>
            )}

            {/* Time */}
            <div className="olm-event-form-row">
              <label>Минута / секунда</label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input type="number" className="olm-select" style={{ width: 80 }} value={eventMinute} onChange={e => setEventMinute(e.target.value)} min="0" placeholder="мин" />
                <span style={{ fontWeight: 700 }}>:</span>
                <input type="number" className="olm-select" style={{ width: 80 }} value={eventSecond} onChange={e => setEventSecond(e.target.value)} min="0" max="59" placeholder="сек" />
              </div>
            </div>
          </div>
          <div className="olm-event-modal-footer">
            <div className="olm-btn olm-btn-secondary" onClick={() => { setEventModalOpen(false); setEditingEventId(null); }}>Отмена</div>
            {isEditing && (
              <div className="olm-btn olm-btn-danger" style={{ padding: '10px 16px' }} onClick={() => { removeEvent(editingEventId); setEventModalOpen(false); setEditingEventId(null); }}>Удалить</div>
            )}
            <div className={`olm-btn ${(eventPlayer && (!isSub || eventSubPlayer)) ? 'olm-btn-primary' : 'olm-btn-disabled'}`} onClick={saveEvent}>
              {isEditing ? 'Сохранить' : 'Добавить'}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ═══════════════════════════════════════
  // MAIN RENDER
  // ═══════════════════════════════════════

  if (!visible || !matchData) return null;

  return (
    <div className="olm-modal-overlay">
      <div className="olm-modal-content">
        <div className="olm-container">
          {/* Header */}
          <div className="olm-header">
            <h2 className="olm-title">Онлайн матч</h2>
            <span className="olm-header-info">{matchData.league_name} — {matchData.home_team_name} vs {matchData.guest_team_name}</span>
            {matchStatus !== 'ended' && matchStatus !== 'half1' && matchStatus !== 'half2' && (
              <div className="olm-header-close" onClick={closeModal}>✕</div>
            )}
          </div>

          {/* Notification */}
          {notification && <div className="olm-notification">{notification}</div>}

          {/* Body */}
          <div className="olm-body">
            {isRestoring ? (
              <div style={{ textAlign: 'center', padding: '40px 0', fontSize: 16, color: '#7f8c8d' }}>
                Загрузка данных матча с сервера...
              </div>
            ) : (
              <>
                {phase === 'roster' && renderRosterPhase()}
                {phase === 'lineup' && renderLineupPhase()}
                {phase === 'live' && renderLivePhase()}
                {phase === 'ended' && renderEndedPhase()}
              </>
            )}
          </div>
        </div>

        {/* Event modal overlay */}
        {eventModalOpen && renderEventModal()}
      </div>
    </div>
  );
}
